/**
 * SearchBar - the one search control the list pages share.
 *
 * Bobbie's ask was blunt: "basically all pages need search bars". With 66 real
 * classes and 36 comp dances in the studio, every list page is a scroll on a
 * phone. Rather than each page growing its own input, this is the shape that
 * Staff and Manage Users already settled on, lifted out so the search on
 * Bulletins feels like the search on Staff.
 *
 * Deliberately dumb: controlled value, no debounce, no matching logic. Each
 * page owns its own fields and scores them with fuzzyMatchScore, because the
 * fields a human searches on are different per page and hiding that in here
 * would make it harder to see, not easier.
 */
import type { CSSProperties } from 'react';

const wrapStyle: CSSProperties = {
  width: '100%',
  maxWidth: 520,
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-1)',
};

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  width: '100%',
};

const inputStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  width: '100%',
  minHeight: 'var(--tap-target)',
  padding: '0 var(--space-3)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-md)',
  font: 'inherit',
  // 16px on purpose. iOS Safari zooms the whole page when it focuses an input
  // rendered under 16px, which on a phone reads as the layout breaking.
  fontSize: 16,
  background: 'var(--white)',
  color: 'var(--ink)',
  boxSizing: 'border-box',
};

const clearBtnStyle: CSSProperties = {
  flexShrink: 0,
  minHeight: 'var(--tap-target)',
  minWidth: 'var(--tap-target)',
  padding: '0 var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--line)',
  background: 'var(--bg)',
  color: 'var(--ink-soft)',
  font: 'inherit',
  fontSize: 14,
  fontWeight: 800,
  cursor: 'pointer',
};

const countStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-soft)',
};

export interface SearchBarProps {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  /** How many rows survive the current search. Pass with totalCount to show a count line. */
  resultCount?: number;
  /** How many rows exist before the search. */
  totalCount?: number;
  /** Defaults to the placeholder, which is already written as a description of the field. */
  ariaLabel?: string;
  style?: CSSProperties;
}

export function SearchBar({
  value,
  onChange,
  placeholder,
  resultCount,
  totalCount,
  ariaLabel,
  style,
}: SearchBarProps) {
  const searching = value.trim().length > 0;
  // The count is only worth the line when it is answering a question the person
  // is actually asking. With no search there is nothing to reassure them about.
  const showCount =
    searching && typeof resultCount === 'number' && typeof totalCount === 'number';

  return (
    <div style={style ? { ...wrapStyle, ...style } : wrapStyle}>
      <div style={rowStyle}>
        <input
          type="search"
          inputMode="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label={ariaLabel ?? placeholder}
          style={inputStyle}
        />
        {searching && (
          <button type="button" style={clearBtnStyle} onClick={() => onChange('')} aria-label="Clear search">
            Clear
          </button>
        )}
      </div>
      {showCount && (
        <div style={countStyle} aria-live="polite">
          {resultCount} of {totalCount}
        </div>
      )}
    </div>
  );
}

export default SearchBar;
